import ExcelJS from "exceljs";

/**
 * Generates and downloads a highly styled Excel report of Expenses directly on the frontend.
 * Clones the exact design patterns of the backend reports, including bottom executive summary blocks.
 * 
 * @param {Array} expenses - Filtered array of expense objects
 * @param {string} periodLabel - Label of the selected period (e.g. "This Month")
 */
export const exportExpensesToExcel = async (expenses, periodLabel) => {
  try {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Expenses Report");

    // Enable grid lines explicitly
    sheet.views = [{ showGridLines: true }];

    // ─── 1. TITLE BANNER (Merged A1:E1) ───
    sheet.mergeCells("A1:E1");
    const titleCell = sheet.getCell("A1");
    titleCell.value = "EXPENSES & OUTFLOWS REPORT";
    titleCell.font = { size: 12, bold: true, color: { argb: "FFFFFFFF" } }; // Lowered font size
    titleCell.alignment = { horizontal: "center", vertical: "middle" };
    titleCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF172554" }, // Deep Navy Blue
    };
    sheet.getRow(1).height = 24; // Reduced row height for less padding

    // ─── 2. PERIOD BANNER (Merged A2:E2) ───
    sheet.mergeCells("A2:E2");
    const periodCell = sheet.getCell("A2");
    periodCell.value = `Period: ${periodLabel || "All Records"}`;
    periodCell.font = { size: 11, italic: true, color: { argb: "FFFFFFFF" } }; // Size 11 matching Sales Report
    periodCell.alignment = { horizontal: "center", vertical: "middle" };
    periodCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF172554" }, // Deep Navy Blue
    };
    sheet.getRow(2).height = 20;

    // Spacer Row 3
    sheet.addRow([]);

    // ─── 3. TABLE HEADERS (Row 4) ───
    const headers = ["Date", "Category", "Description", "Amount (₹)", "Status"];
    const headerRow = sheet.addRow(headers);
    headerRow.height = 24;
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF1B365D" }, // Corporate Blue
      };
      cell.alignment = { vertical: "middle", horizontal: "center" };
      cell.border = {
        bottom: { style: "medium", color: { argb: "FF172554" } },
      };
    });

    // ─── 4. DATA ROWS (Row 5 onwards) ───
    expenses.forEach((item) => {
      const row = sheet.addRow([
        item.date,
        item.category,
        item.description,
        Number(item.amount || 0),
        item.status,
      ]);
      row.height = 20;

      // Alignments & Number formats
      row.getCell(1).alignment = { horizontal: "center", vertical: "middle" }; // Date
      row.getCell(2).alignment = { horizontal: "center", vertical: "middle" }; // Category
      row.getCell(3).alignment = { horizontal: "left", vertical: "middle" };   // Description
      row.getCell(4).alignment = { horizontal: "right", vertical: "middle" };  // Amount
      row.getCell(4).numFormat = '"₹"#,##0';
      row.getCell(5).alignment = { horizontal: "center", vertical: "middle" }; // Status
      
      // Light borders for data rows
      row.eachCell((cell) => {
        cell.border = {
          bottom: { style: "thin", color: { argb: "FFE2E8F0" } },
        };
      });
    });

    // ─── 5. TOTALS ROW (Soft blue-slate, double borders) ───
    const lastDataRow = sheet.lastRow.number;
    
    // We add a styled row for total sum (starts at row 5)
    const totalRow = sheet.addRow([
      "TOTAL",
      "",
      "",
      expenses.length > 0 ? { formula: `SUM(D5:D${lastDataRow})` } : 0,
      "",
    ]);
    totalRow.height = 24;

    totalRow.eachCell((cell, colIndex) => {
      cell.font = { bold: true, color: { argb: "FF172554" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFF1F5F9" }, // Soft blue-slate
      };
      
      // Accounting borders
      cell.border = {
        top: { style: "thin", color: { argb: "FF1B365D" } },
        bottom: { style: "double", color: { argb: "FF1B365D" } },
      };
      cell.alignment = { vertical: "middle" };

      if (colIndex === 1) {
        cell.alignment = { horizontal: "center", vertical: "middle" };
      } else if (colIndex === 4) {
        cell.alignment = { horizontal: "right", vertical: "middle" };
        cell.numFormat = '"Total: "₹#,##0';
      }
    });

    // ─── 6. EXECUTIVE SUMMARY TABLE (Row lastDataRow + 4) ───
    // Separated by 3 blank rows below the main table
    const startSummaryRow = lastDataRow + 4;
    
    const totalOutflow = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);
    const totalPaid = expenses.filter(e => e.status === "Paid").reduce((s, e) => s + Number(e.amount || 0), 0);
    const totalPending = expenses.filter(e => e.status === "Pending").reduce((s, e) => s + Number(e.amount || 0), 0);
    const entryCount = expenses.length;
    const avgExpense = entryCount > 0 ? Number((totalOutflow / entryCount).toFixed(2)) : 0;

    // Header Row for Summary Table
    sheet.mergeCells(startSummaryRow, 1, startSummaryRow, 4);
    const summaryHeader = sheet.getCell(startSummaryRow, 1);
    summaryHeader.value = "EXECUTIVE SUMMARY";
    summaryHeader.font = { size: 10, bold: true, color: { argb: "FFFFFFFF" } };
    summaryHeader.alignment = { horizontal: "center", vertical: "middle" };
    summaryHeader.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF1B365D" }, // Corporate Navy
    };
    sheet.getRow(startSummaryRow).height = 26;

    const categoryMap = expenses.reduce((acc, e) => {
      const cat = e.category || "Others";
      acc[cat] = (acc[cat] || 0) + Number(e.amount || 0);
      return acc;
    }, {});

    const sortedCategories = Object.entries(categoryMap).sort((a, b) => b[1] - a[1]);

    const summaryRows = [
      { type: "title", text: "EXPENSE PERFORMANCE" },
      { type: "metric", label: "Gross Outflow", value: totalOutflow, format: '"₹"#,##0.00' },
      { type: "metric", label: "Total Paid", value: totalPaid, format: '"₹"#,##0.00' },
      { type: "metric", label: "Total Pending", value: totalPending, format: '"₹"#,##0.00' },
      { type: "title", text: "CATEGORY BREAKDOWN" }
    ];

    sortedCategories.forEach(([catName, catVal]) => {
      summaryRows.push({
        type: "metric",
        label: catName,
        value: catVal,
        format: '"₹"#,##0.00'
      });
    });


    summaryRows.push({ type: "title", text: "OPERATIONAL METRICS" });
    summaryRows.push({ type: "metric", label: "Total Entries", value: entryCount, format: '#,##0' });
    summaryRows.push({ type: "metric", label: "Average Expense", value: avgExpense, format: '"₹"#,##0.00' });

    summaryRows.forEach((item, idx) => {
      const rNum = startSummaryRow + (idx + 1);
      sheet.getRow(rNum).height = 20;

      if (item.type === "title") {
        sheet.mergeCells(rNum, 1, rNum, 4);
        const cell = sheet.getCell(rNum, 1);
        cell.value = item.text;
        cell.font = { size: 9, bold: true, color: { argb: "FF1E293B" } };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        
        for (let c = 1; c <= 4; c++) {
          const tc = sheet.getCell(rNum, c);
          tc.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFF1F5F9" } // Light blue-slate matching sales report title rows
          };
          tc.border = {
            top: { style: "thin", color: { argb: "FFCBD5E1" } },
            bottom: { style: "thin", color: { argb: "FFCBD5E1" } },
            left: { style: "thin", color: { argb: "FFCBD5E1" } },
            right: { style: "thin", color: { argb: "FFCBD5E1" } }
          };
        }
      } else if (item.type === "metric") {
        // Label (Col 1 & 2 merged)
        sheet.mergeCells(rNum, 1, rNum, 2);
        const lblCell = sheet.getCell(rNum, 1);
        lblCell.value = item.label;
        lblCell.font = { size: 9, color: { argb: "FF475569" }, bold: true };
        lblCell.alignment = { horizontal: "left", vertical: "middle" };

        // Value (Col 3 & 4 merged)
        sheet.mergeCells(rNum, 3, rNum, 4);
        const valCell = sheet.getCell(rNum, 3);
        valCell.value = item.value;
        valCell.font = { size: 9, color: { argb: "FF1E293B" }, bold: true };
        valCell.alignment = { horizontal: "right", vertical: "middle" };
        if (item.format) {
          valCell.numFormat = item.format;
        }

        // Light thin borders for metrics
        for (let c = 1; c <= 4; c++) {
          const mc = sheet.getCell(rNum, c);
          mc.border = {
            top: { style: "thin", color: { argb: "FFE2E8F0" } },
            bottom: { style: "thin", color: { argb: "FFE2E8F0" } },
            left: { style: "thin", color: { argb: "FFE2E8F0" } },
            right: { style: "thin", color: { argb: "FFE2E8F0" } }
          };
        }
      }
    });

    // ─── 7. AUTO-FIT COLUMN WIDTHS (Excluding Header & Summary rows to prevent stretching) ───
    sheet.columns.forEach((column) => {
      let maxLen = 12;
      column.eachCell({ includeEmpty: true }, (cell) => {
        // Skip title banners and bottom summary table rows from calculating width
        if (cell.row <= 3 || cell.row >= startSummaryRow) return;
        if (cell.value) {
          const valStr = typeof cell.value === "object" && cell.value.formula
            ? "Total: ₹99,99,999"
            : String(cell.value);
          if (valStr.length > maxLen) {
            maxLen = valStr.length;
          }
        }
      });
      column.width = maxLen + 4; // Add extra padding
    });

    // ─── 8. GENERATE BLOB & DOWNLOAD ───
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    
    // Clean period label for filename
    const cleanPeriod = (periodLabel || "All_Time").replace(/\s+/g, "_");
    link.download = `ExpensesReport_${cleanPeriod}.xlsx`;
    
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

  } catch (err) {
    console.error("Failed to generate Excel sheet:", err);
    alert("Failed to generate Excel sheet. Check console logs for errors.");
  }
};
